import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CompatClient, IFrame, Stomp, StompSubscription } from '@stomp/stompjs';
import { BehaviorSubject, combineLatest, filter, firstValueFrom } from 'rxjs';
import { UserComplete } from 'src/app/rgs/components/company-data/user/models/user-complete.model';
import { Format } from 'src/app/rgs/components/objects/object-item/object-inspection/components/print-dialog/models/print-format';
import { PermissionsService } from 'src/app/shared/services/permissions.service';
import { ArrayUtils } from 'src/app/shared/utils/array.utils';
import { environment } from 'src/environments/environment';
import { CommonState } from '../../../../../shared/services/common-state.service';
import { RemoveReportTaskResponse } from '../models/remove-report-task-response.model';
import { Task, TaskReportType, TaskStatus } from '../models/task.model';

export class ShareOptions {
  public ids: Set<string> = new Set<string>();
  public emails: Set<string> = new Set<string>();
  public description: string | null = null;
}

@Injectable({
  providedIn: 'root',
})
export class TaskService extends CommonState<Task> {
  private readonly http = inject(HttpClient);
  private readonly permissionsService = inject(PermissionsService);

  public taskSidebarVisible$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public runningTaskId: string | undefined;
  public taskStatus$: BehaviorSubject<Task | undefined> = new BehaviorSubject<Task | undefined>(undefined);

  private queueTaskSubscription?: StompSubscription;
  private runningTaskSubscription?: StompSubscription;
  private finishTaskSubscription?: StompSubscription;
  private downloadedTaskSubscription?: StompSubscription;
  private deleteTaskSubscription?: StompSubscription;

  private userId: string | undefined;
  private license: string | undefined;
  private user: UserComplete | undefined;

  public stompClient?: CompatClient;

  constructor() {
    super(Task);
    this.initTaskSocket();
  }

  public initTaskSocket() {
    this.stompClient = Stomp.client(`${environment.springAppUrl.replace('http', 'ws')}/reports-ws`);
    this.stompClient.debug = () => {};

    combineLatest([this.permissionsService.license$, this.permissionsService.user$])
      .pipe(filter(([license, user]) => !!license && !!user))
      .subscribe(async ([license, user]) => {
        if (!license || !this.stompClient || !user) {
          return;
        }

        this.license = license;
        this.userId = user.userId;
        this.user = user;

        const tasks = await this.findAllTasks();
        this.setTasks(tasks);

        if (this.stompClient?.connected) {
          this.stompClient.disconnect();
        }

        this.stompClient.connect({}, () => {
          if (!this.stompClient) {
            return;
          }

          this.createAllSockets();

          this.stompClient.onStompError = (frame: IFrame) => {
            console.log(frame);
          };
        });
      });
  }

  public createAllSockets() {
    if (!this.stompClient) {
      return;
    }
    this.queueTaskSubscription = this.stompClient.subscribe(
      `/reports-ws/${this.license}/queue/${this.userId}`,
      (msgOut) => {
        const task: Task = JSON.parse(msgOut.body);

        this._items$.next([...this._items$.value, task]);
      },
    );

    this.runningTaskSubscription = this.stompClient.subscribe(
      `/reports-ws/${this.license}/running/${this.userId}`,
      (msgOut) => {
        const task: Task = JSON.parse(msgOut.body);

        this._items$.next(this._items$.value.map((t) => (t.uuid === task.uuid ? task : t)));
        this.runningTaskId = task.uuid;

        if (task.userId === this.userId) {
          this.taskStatus$.next(task);
        }
      },
    );

    const hasObjectFilter = ArrayUtils.ensureArray(this.user?.rgsUser?.objectfilters).length > 0;
    const hasClientFilter = ArrayUtils.ensureArray(this.user?.rgsUser?.opdrachtgeverfilters).length > 0;
    const hasAnyFilter = hasObjectFilter || hasClientFilter;
    const finishTaskSubscriptionEndpoint = hasAnyFilter
      ? `/reports-ws/${this.license}/finish/${this.userId}`
      : `/reports-ws/${this.license}/finish`;

    this.finishTaskSubscription = this.stompClient.subscribe(finishTaskSubscriptionEndpoint, (msgOut) => {
      const task: Task = JSON.parse(msgOut.body);
      const taskIndex = this._items$.value.findIndex((t) => t.uuid === task.uuid);

      if (taskIndex !== -1) {
        this._items$.next(this._items$.value.map((t) => (t.uuid === task.uuid ? task : t)));
      } else {
        this._items$.next([...this._items$.value, task]);
      }

      if (task.userId === this.userId) {
        this.taskStatus$.next(task);
      }
    });

    this.downloadedTaskSubscription = this.stompClient.subscribe(`/reports-ws/${this.license}/downloaded`, (msgOut) => {
      const task: Task = JSON.parse(msgOut.body);

      this._items$.next(this._items$.value.map((t) => (t.uuid === task.uuid ? task : t)));
    });

    this.deleteTaskSubscription = this.stompClient.subscribe(`/reports-ws/${this.license}/delete`, (msgOut) => {
      const task: Task = JSON.parse(msgOut.body);

      this._items$.next(this._items$.value.filter((t) => t.uuid !== task.uuid));
    });
  }

  public closeAllTaskSockets() {
    if (this.queueTaskSubscription) {
      this.queueTaskSubscription.unsubscribe();
    }

    if (this.runningTaskSubscription) {
      this.runningTaskSubscription.unsubscribe();
    }

    if (this.finishTaskSubscription) {
      this.finishTaskSubscription.unsubscribe();
    }

    if (this.downloadedTaskSubscription) {
      this.downloadedTaskSubscription.unsubscribe();
    }

    if (this.deleteTaskSubscription) {
      this.deleteTaskSubscription.unsubscribe();
    }
  }

  public findAllTasksAdmin() {
    return this.http.get<Task[]>(`${environment.springAppUrl}/report/tasks/all`);
  }

  public findTaskById(uuid: string) {
    return this.http.get<Task>(`${environment.springAppUrl}/report/tasks/findById/${uuid}`);
  }

  public deleteTaskAdmin(uuid: string) {
    return this.http.delete<RemoveReportTaskResponse>(`${environment.springAppUrl}/report/tasks/${uuid}`);
  }

  public moveToFirstInQueue(uuid: string) {
    return this.http.post(`${environment.springAppUrl}/report/tasks/moveTaskToTop/${uuid}`, {});
  }

  public async findAllTasks() {
    return await firstValueFrom(this.http.get<Task[]>(`${environment.springAppUrl}/report/tasks`));
  }

  public setTasks(tasks: Task[]) {
    const runningTask = tasks.find((task) => task.reportStatus === TaskStatus.RUNNING);
    if (runningTask) {
      this.taskStatus$.next(runningTask);
      this.runningTaskId = runningTask.uuid;
    }
    this._items$.next(tasks);
  }

  public downloadResult(task: Task | string) {
    const uuid = typeof task === 'string' ? task : task.uuid;
    return this.http.get(`${environment.springAppUrl}/report/tasks/data/${uuid}`, { responseType: 'blob' });
  }

  public downloadResultByFields(id: string, type: TaskReportType, format: Format, name: string) {
    return this.http.get(`${environment.springAppUrl}/report/tasks/data/${id}/${type}/${format}/${name}`, {
      responseType: 'blob',
    });
  }

  public downloadResults(tasks: Task[]) {
    let uuids = '';

    tasks
      .filter((task) => !!task.uuid)
      .map((task) => task.uuid)
      .forEach((id) => {
        uuids += id + ',';
      });

    uuids = uuids.substring(0, uuids.length - 1);

    return this.http.get(`${environment.springAppUrl}/report/tasks/download?uuids=${uuids}`, {
      responseType: 'blob',
    });
  }

  public async deleteTask(task: Task) {
    const response = await firstValueFrom(
      this.http.delete<RemoveReportTaskResponse>(`${environment.springAppUrl}/report/tasks/data/${task.uuid}`),
    );

    if (response === RemoveReportTaskResponse.SUCCESS) {
      this._items$.next(this._items$.value.filter((t) => t.uuid !== task.uuid));
    }

    return response;
  }

  public shareTask(shareOptions: ShareOptions) {
    return this.http.post(`${environment.springAppUrl}/report/tasks/share`, {
      ...shareOptions,
      ids: Array.from(shareOptions.ids),
      emails: Array.from(shareOptions.emails),
    });
  }
}
